/**
 * The test sheets a job can fill in, on the job card (COMPLY-1).
 *
 * A panel is tested in a plant room, which is the worst reception in the
 * building, so this reads from the device's own store and writes through
 * `actions.submitTestSheet`, which queues when the server cannot be reached.
 *
 * Three decisions worth keeping:
 *
 *  * **Nothing submits half-finished.** The button stays disabled until every
 *    non-optional question is answered, and the card says how many are left.
 *    A sheet nobody finished is not a pass, and the server refuses it either
 *    way — better to say so before the tech walks away from the panel.
 *  * **"N/A" is a first-class answer** on a pass/fail question. A strobe that
 *    was never fitted is not a failure, and a tech with nowhere to put that
 *    ticks "pass" instead, which is how a register starts lying.
 *  * **No certificate number offline.** The number comes from a sequence only
 *    the server holds, so a queued sheet says the certificate is issued when
 *    it sends rather than inventing one (D15).
 */
import { useMemo, useState } from "react";

import * as actions from "../lib/actions";
import { ApiError } from "../lib/api";
import { day } from "../lib/format";
import { useLocal } from "../lib/local";
import * as repo from "../lib/repo";
import type { Row } from "../lib/store";
import type { FormField, FormSchema } from "../lib/types";
import { Card, Chip } from "./ui";

type Said = { tone: "ok" | "info" | "crit"; text: string };

/** Every question on a sheet, flattened — mirrors `compliance.fields_of`. */
function fieldsOf(schema: FormSchema | undefined): FormField[] {
  return (schema?.sections ?? []).flatMap((section) => section.fields ?? []);
}

export function TestSheets({ jobId }: { jobId: string }) {
  const { data, reload } = useLocal(() => repo.testable(jobId), [jobId]);
  const [open, setOpen] = useState<string | null>(null);
  const [said, setSaid] = useState<Said | null>(null);

  // No assets at this site, or no sheet that applies to them. Say nothing
  // rather than showing an empty card on a labour-only call.
  if (!data || data.length === 0) return null;

  return (
    <Card title="Test sheets" subtitle={`${data.length} on this site`} padded={false}>
      {said && (
        <p
          className={`notice notice-${said.tone}`}
          role={said.tone === "crit" ? "alert" : "status"}
          style={{ margin: 12 }}
        >
          {said.text}
        </p>
      )}

      <ul className="register">
        {data.map(({ asset, template, last }) => (
          <li key={String(asset.id)} className="register-row">
            <SheetRow
              asset={asset}
              template={template as Row}
              last={last}
              jobId={jobId}
              isOpen={open === String(asset.id)}
              onOpen={() => {
                setOpen(open === String(asset.id) ? null : String(asset.id));
                setSaid(null);
              }}
              onDone={(result) => {
                setSaid(result);
                setOpen(null);
                reload();
              }}
              onRefused={(text) => setSaid({ tone: "crit", text })}
            />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function SheetRow({
  asset,
  template,
  last,
  jobId,
  isOpen,
  onOpen,
  onDone,
  onRefused,
}: {
  asset: Row;
  template: Row;
  last: Row | null;
  jobId: string;
  isOpen: boolean;
  onOpen: () => void;
  onDone: (said: Said) => void;
  onRefused: (text: string) => void;
}) {
  const schema = template.schema as FormSchema | undefined;
  const fields = useMemo(() => fieldsOf(schema), [schema]);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [notes, setNotes] = useState("");
  const [signedBy, setSignedBy] = useState("");
  const [busy, setBusy] = useState(false);
  const needsSignature = schema?.requires_signature === true;

  // A tick box is answered by being a tick box: "battery replaced: no" is what
  // an untouched one means, and making the tech toggle it on and off to say so
  // is how a sheet gets ticked without being read.
  const unanswered = fields.filter(
    (f) => !f.optional && f.type !== "bool" && (answers[f.key] === undefined || answers[f.key] === ""),
  );
  const failed = fields.filter((f) => f.type === "pass_fail" && answers[f.key] === "fail");

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (unanswered.length > 0 || busy || (needsSignature && !signedBy.trim())) return;
    setBusy(true);
    try {
      const { result, queued, message } = await actions.submitTestSheet({
        template_id: String(template.id),
        asset_id: String(asset.id),
        job_id: jobId,
        answers: Object.fromEntries(
          fields.map((f) => [f.key, f.type === "bool" ? answers[f.key] === true : answers[f.key]]),
        ),
        notes,
        signed_by: signedBy.trim() || null,
      });
      onDone({
        tone: queued ? "info" : result?.result === "fail" ? "crit" : "ok",
        text:
          message ??
          (result?.certificate
            ? `${String(asset.ref)} passed — certificate ${result.certificate.number} issued, next test ${day(result.next_test_due_on)}.`
            : result?.result === "fail"
              ? `${String(asset.ref)} failed. No certificate issued, and it's now showing as a fault.`
              : `${String(asset.ref)} recorded.`),
      });
    } catch (err) {
      onRefused(err instanceof ApiError ? err.message : "Couldn't record that test sheet.");
    } finally {
      setBusy(false);
    }
  }

  const due = asset.next_test_due_on ? String(asset.next_test_due_on) : null;
  const overdue = due !== null && due < new Date().toISOString().slice(0, 10);

  if (!isOpen) {
    return (
      <>
        <button type="button" className="register-head register-closed" onClick={onOpen}>
          <b className="mono">{String(asset.ref)}</b>
          <span className="tiny muted">{String(template.name)}</span>
          {due ? (
            <Chip tone={overdue ? "crit" : "warn"}>{overdue ? `due ${day(due)}` : `next ${day(due)}`}</Chip>
          ) : (
            <Chip>never tested</Chip>
          )}
        </button>
        {last && (
          <div className="tiny muted">
            Last: {String(last.result ?? "recorded").replace(/_/g, " ")}
            {last.submitted_at ? ` on ${day(String(last.submitted_at).slice(0, 10))}` : ""}
          </div>
        )}
      </>
    );
  }

  return (
    <form className="register-open" onSubmit={(e) => void submit(e)}>
      <div className="register-head">
        <b className="mono">{String(asset.ref)}</b>
        <span className="tiny muted">{String(template.name)}</span>
      </div>

      {(schema?.sections ?? []).map((section, i) => (
        <div key={section.title ?? i}>
          {section.title && <p className="label">{section.title}</p>}
          {(section.fields ?? []).map((field) => (
            <Question
              key={field.key}
              field={field}
              value={answers[field.key]}
              onChange={(value) => setAnswers((a) => ({ ...a, [field.key]: value }))}
            />
          ))}
        </div>
      ))}

      <label className="field">
        <span>Notes (optional)</span>
        <textarea
          value={notes}
          rows={2}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything the customer should know before the next visit"
        />
      </label>

      {failed.length > 0 && (
        <p className="notice notice-crit" role="status">
          {failed.length === 1 ? `${failed[0]!.label} failed` : `${failed.length} checks failed`} — this sheet won't
          issue a certificate, and {String(asset.ref)} will show as a fault.
        </p>
      )}

      {needsSignature && (
        <label className="field">
          <span>Sign — your full name</span>
          <input
            value={signedBy}
            onChange={(e) => setSignedBy(e.target.value)}
            placeholder="Dan Whitmore"
            autoComplete="name"
          />
          <span className="tiny muted">
            Typing your name certifies that you carried out this test. It is printed on the certificate.
          </span>
        </label>
      )}

      <div className="btn-row">
        <button
          className="btn btn-primary"
          type="submit"
          disabled={busy || unanswered.length > 0 || (needsSignature && !signedBy.trim())}
        >
          {busy ? "Recording…" : "Submit test sheet"}
        </button>
        <span className="tiny muted">
          {unanswered.length === 0
            ? needsSignature && !signedBy.trim()
              ? "Everything answered — sign it to submit"
              : "Everything answered"
            : `${unanswered.length} still to answer: ${unanswered
                .slice(0, 3)
                .map((f) => f.label)
                .join(", ")}`}
        </span>
      </div>
    </form>
  );
}

function Question({
  field,
  value,
  onChange,
}: {
  field: FormField;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const label = (
    <span>
      {field.label}
      {field.unit ? ` (${field.unit})` : ""}
      {field.optional ? " — optional" : ""}
    </span>
  );

  if (field.type === "pass_fail") {
    return (
      <div className="field">
        {label}
        <div className="segmented" role="group" aria-label={field.label}>
          {(
            [
              ["pass", "Pass"],
              ["fail", "Fail"],
              ["na", "N/A"],
            ] as const
          ).map(([key, text]) => (
            <button
              key={key}
              type="button"
              className={value === key ? "active" : ""}
              onClick={() => onChange(value === key ? "" : key)}
            >
              {text}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === "bool") {
    return (
      <label className="field" style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
    );
  }

  if (field.type === "choice") {
    return (
      <label className="field">
        {label}
        <select value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(field.choices ?? []).map((choice) => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <label className="field">
      {label}
      <input
        value={value === undefined || value === null ? "" : String(value)}
        inputMode={field.type === "number" ? "decimal" : "text"}
        onChange={(e) =>
          onChange(field.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)
        }
      />
    </label>
  );
}
